import React, { useEffect, useMemo, useState } from "react";
import { FlatList, Pressable, StyleSheet, View } from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../types/navigationParams";
import { Button, Card, Chip, List, Text, useTheme } from "react-native-paper";
import { OutlinedInput } from "../../components/fields/OutlinedInput";
import {
  getCompanyDocuments,
  getDocuments,
  Where,
} from "../../services/firestoreServices";
import type { PickerClientFilterKey } from "../../config/companies/config";
import { useAppSelector } from "../../redux/store";
import { useTranslation } from "react-i18next";
import { applyPickerClientFilter } from "../../utils/pickerClientFilters";
import { getNestedValue } from "../../utils/utils";

type Props = NativeStackScreenProps<
  RootStackParamList,
  "CollectionPickerScreen"
>;

type Item = Record<string, any>;
type Items = Item[];

const getQueryOptions = (
  whereOptions: Where,
  sources: Record<string, Record<string, any> | undefined>,
) => {
  const resolvedWhere = [];

  for (const item of whereOptions) {
    if (!item.from) {
      resolvedWhere.push(item);
      continue;
    }

    const source = sources[item.from];
    const filterValue = getNestedValue(source, String(item.value));
    if (filterValue === undefined || filterValue === null) {
      return null;
    }

    resolvedWhere.push({
      ...item,
      value: filterValue,
    });
  }

  return {
    where: resolvedWhere,
  };
};

const sortedByAlphabet = (data: any[], key: string) => {
  return [...data].sort((a, b) => a[key].localeCompare(b[key]));
};

const useFilteredData = (
  rawData: Items,
  alreadySelected: Items,
  searchQuery: string,
  fieldKey: string,
) => {
  return useMemo(() => {
    const selectedDataExcluded = rawData.filter(
      (item: Record<string, any>) =>
        !alreadySelected.some((selectedItem) => selectedItem.id === item.id),
    );

    if (searchQuery === "") {
      return selectedDataExcluded;
    }

    return selectedDataExcluded.filter((item) =>
      item[fieldKey]?.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [alreadySelected, rawData, searchQuery, fieldKey]);
};

export type CollectionPickerOptions = {
  multiselect?: boolean;
  onValueChange: (value: any) => void;
  collection: string;
  key: string;
  secondKey?: string;
  thirdKey?: string;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  showAdd?: string[];
  selectedOptions?: any[];
  where?: Where;
  onlyCompany?: boolean;
  formData: Record<string, any>;
  clientFilter?: PickerClientFilterKey;
};

export default function CollectionPickerScreen({ route, navigation }: Props) {
  const {
    collection,
    onValueChange,
    key = "name",
    secondKey,
    thirdKey,
    autoCapitalize = "none",
    multiselect = false,
    showAdd = [],
    selectedOptions = [],
    onlyCompany = true,
    where = [],
    formData,
    clientFilter,
  } = route.params;

  const state = useAppSelector((state) => state.auth);
  const config = useAppSelector((state) => state.auth.config);

  const [searchQuery, setSearchQuery] = useState("");
  const [rawData, setRawData] = useState<Items>([]);
  const [alreadySelected, setAlreadySelected] = useState<Items>(
    Array.isArray(selectedOptions) ? selectedOptions : [],
  );

  const { t } = useTranslation();
  const { colors } = useTheme();

  const filteredData = useFilteredData(
    rawData,
    alreadySelected,
    searchQuery,
    key,
  );

  const configCollection = config.database.find(
    (item) => item.name === collection,
  )!;
  const fields = configCollection?.fields;

  const canAdd = showAdd?.length === 0 || showAdd?.includes(state.user.role);

  useEffect(() => {
    if (!collection) throw new Error("Collection is not specified in a config");

    const getFunction = onlyCompany ? getCompanyDocuments : getDocuments;

    const fetchData = async () => {
      try {
        const queryOptions = getQueryOptions(where, {
          auth: state,
          formData,
          user: state.user,
        });
        if (!queryOptions) {
          setRawData([]);
          return;
        }

        let data = await getFunction(collection, queryOptions);
        if (clientFilter) {
          data = applyPickerClientFilter(data, clientFilter, { formData });
        }

        const sortedData = sortedByAlphabet(data, key);
        setRawData(sortedData);
      } catch (error) {
        console.error(
          "[COLLECTION PICKER],Error fetching collection data:",
          error,
        );
      }
    };

    void fetchData();
  }, [clientFilter, collection, formData, key, onlyCompany, where]);

  const onSelectItem = (newValue: any, isNewDocument?: boolean) => {
    if (multiselect) {
      setAlreadySelected((prev) => [...prev, newValue]);
      if (isNewDocument) {
        onValueChange([newValue]);
        navigation.goBack();
        return;
      }
    } else {
      onValueChange(newValue);
      navigation.goBack();
    }
  };

  const onDelete = (id: string) => {
    setAlreadySelected((prev) => prev.filter((item) => item.id !== id));
  };

  const onChangeText = (text: string) => {
    setSearchQuery(
      autoCapitalize === "characters" ? text.toUpperCase() : text,
    );
  };

  const navigateToForm = () => {
    navigation.replace("FormScreen", {
      item: undefined,
      collection: collection,
      fields: fields,
      onNewDocumentCallback: (doc) => onSelectItem(doc, true),
    });
  };

  return (
    <View style={styles.overlay}>
      <Pressable onPress={() => navigation.goBack()} style={{ flex: 1 }} />
      <Card style={styles.card}>
        <Card.Content style={{ maxHeight: 500 }}>
          {canAdd && (
            <Button
              mode="contained-tonal"
              onPress={navigateToForm}
              style={styles.buttonAddNew}
            >
              {t("Add new")}
            </Button>
          )}

          <View style={styles.chipContainer}>
            {alreadySelected.map((item) => (
              <Chip
                key={item.id}
                onClose={() => onDelete(item.id)}
                style={styles.chip}
                textStyle={styles.chipText}
              >
                {item[key]}
              </Chip>
            ))}
          </View>
          <OutlinedInput
            label="Search"
            testID="search"
            style={styles.searchInput}
            value={searchQuery}
            autoCapitalize={autoCapitalize}
            autoFocus
            onChangeText={onChangeText}
          />
          <FlatList
            style={[
              styles.flatList,
              {
                borderColor: colors.outlineVariant,
                backgroundColor: colors.secondaryContainer,
              },
            ]}
            data={filteredData}
            ListEmptyComponent={() => (
              <View style={styles.emptyList}>
                <Text style={styles.emptyListText}>{t("Nothing found")}</Text>
              </View>
            )}
            renderItem={({ item }) => (
              <List.Item
                title={item[key]}
                description={
                  secondKey
                    ? thirdKey
                      ? `${t(item[secondKey])} - ${item[thirdKey]}`
                      : t(item[secondKey])
                    : thirdKey
                      ? item[thirdKey]
                      : undefined
                }
                onPress={() => onSelectItem(item)}
              />
            )}
            keyExtractor={(item, index) => item.id || index.toString()}
          />
          {multiselect && (
            <Button
              mode="contained"
              testID="save"
              style={styles.buttonSave}
              onPress={() => {
                onValueChange(alreadySelected);
                navigation.goBack();
              }}
            >
              Save
            </Button>
          )}
        </Card.Content>
      </Card>
      <Pressable onPress={() => navigation.goBack()} style={{ flex: 1 }} />
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
  },
  card: {
    margin: 20,
    paddingBottom: 10,
    maxWidth: 500,
    alignSelf: "center",
    width: "100%",
  },
  searchInput: {},
  option: {
    padding: 10,
  },
  flatList: {
    borderWidth: 1,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    maxHeight: 300,
  },
  chipContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    rowGap: 10,
    marginVertical: 8,
  },
  chip: {
    marginRight: 10,
  },
  chipText: {
    fontSize: 12,
  },
  emptyList: {
    padding: 15,
  },
  emptyListText: {
    textAlign: "center",
  },
  buttonAddNew: {
    alignSelf: "flex-end",
  },
  buttonSave: {
    marginTop: 15,
  },
});
